import { Add } from "@mui/icons-material";
import { Box, Button, Stack, Typography } from "@mui/material";
import ExpenseGuideLinks from "../ExpenseGuideLinks";

const currencyFormatter = new Intl.NumberFormat("el-GR", {
  style: "currency",
  currency: "EUR",
});

const formatCurrency = (value) => currencyFormatter.format(Number(value ?? 0));

const formatValue = (value) => {
  if (value === undefined || value === null || value === "") return "-";
  return value;
};

const SubtitleItem = ({ label, value }) => (
  <Stack direction="row" spacing={0.75} alignItems="baseline">
    <Typography
      component="span"
      sx={{ color: "#5f6368", fontSize: 13, fontWeight: 600 }}
    >
      {label}:
    </Typography>
    <Typography
      component="span"
      sx={{ color: "#212529", fontSize: 13, fontWeight: 700 }}
    >
      {formatValue(value)}
    </Typography>
  </Stack>
);

const HeaderDivider = () => (
  <Box
    sx={{
      width: "1px",
      height: 18,
      backgroundColor: "rgba(0, 0, 0, 0.2)",
      alignSelf: "center",
      display: { xs: "none", sm: "block" },
    }}
  />
);

const SummaryTile = ({ label, value, currency = true }) => (
  <Box
    sx={{
      px: 1.25,
      py: 0.85,
      height: "100%",
      border: "1px solid rgba(0, 0, 0, 0.125)",
      borderRadius: "0.25rem",
      backgroundColor: "rgba(0, 0, 0, 0.02)",
      display: "grid",
      gridTemplateColumns: "minmax(0, 1fr) auto",
      gap: 1,
      alignItems: "center",
    }}
  >
    <Typography
      sx={{
        color: "#5f6368",
        fontSize: 12,
        fontWeight: 600,
        lineHeight: 1.2,
        minWidth: 0,
      }}
    >
      {label}:
    </Typography>
    <Typography
      sx={{
        color: "#212529",
        fontSize: 16,
        fontWeight: 700,
        lineHeight: 1.25,
        textAlign: "right",
        whiteSpace: "nowrap",
      }}
    >
      {currency ? formatCurrency(value) : formatValue(value)}
    </Typography>
  </Box>
);

const MhdExpensesHeader = ({ applicationDetails, onAddExpense }) => {
  const canAdd = applicationDetails?.settings?.canAdd === true;

  return (
    <Box
      sx={{
        border: "1px solid rgba(0, 0, 0, 0.125)",
        borderRadius: "0.25rem",
        backgroundColor: "#fff",
      }}
    >
      <Stack
        direction={{ xs: "column", md: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "stretch", md: "center" }}
        spacing={1.5}
        sx={{
          px: 2,
          py: 1.25,
          borderBottom: "1px solid rgba(0, 0, 0, 0.125)",
          backgroundColor: "rgba(0, 0, 0, 0.03)",
        }}
      >
        <Stack
          direction="row"
          spacing={2}
          alignItems="baseline"
          flexWrap="wrap"
          useFlexGap
        >
          <Typography
            component="h1"
            sx={{ color: "#212529", fontSize: 18, fontWeight: 700 }}
          >
            {formatValue(applicationDetails?.name)}
          </Typography>
          <HeaderDivider />
          <Stack
            direction="row"
            spacing={2}
            flexWrap="wrap"
            useFlexGap
          >
            <SubtitleItem
              label="Κατάσταση"
              value={applicationDetails?.status}
            />
            <HeaderDivider />
            <SubtitleItem
              label="Ημ/νία αίτησης"
              value={applicationDetails?.applicationDateFormatted}
            />
          </Stack>
        </Stack>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1.5}
          alignItems={{ xs: "flex-start", sm: "center" }}
          justifyContent="flex-end"
          sx={{
            alignSelf: { xs: "flex-start", md: "center" },
            width: { xs: "100%", md: "auto" },
          }}
        >
          <ExpenseGuideLinks
            sx={{
              justifyContent: { xs: "flex-start", sm: "flex-end" },
              width: "auto",
            }}
          />
          {canAdd && (
            <Button
              size="small"
              variant="contained"
              startIcon={<Add />}
              onClick={onAddExpense}
              title="Νέα Δαπάνη (Alt+N)"
              sx={{ whiteSpace: "nowrap" }}
            >
              Νέα Δαπάνη
            </Button>
          )}
        </Stack>
      </Stack>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "repeat(2, minmax(0, 1fr))",
            md: "repeat(3, minmax(0, 1fr))",
            lg: "repeat(6, minmax(0, 1fr))",
          },
          gap: 1,
          p: 1.5,
        }}
      >
        <SummaryTile
          label="Πλήθος Δαπανών"
          value={applicationDetails?.expensesCount ?? 0}
          currency={false}
        />
        <SummaryTile
          label="Προϋπολογισμός"
          value={applicationDetails?.budgetAmount}
        />
        <SummaryTile label="Δαπάνες" value={applicationDetails?.expensesAmount} />
        <SummaryTile
          label="Εγκεκριμένο"
          value={applicationDetails?.approvedAmount}
        />
        <SummaryTile label="Πληρωμένο" value={applicationDetails?.paidAmount} />
        <SummaryTile
          label="Διαθέσιμο"
          value={applicationDetails?.availableAmount}
        />
      </Box>
    </Box>
  );
};

export default MhdExpensesHeader;
